import { Plus } from 'lucide-react';
import { toast } from 'sonner';

import { QueryError } from '@/components/QueryError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { apiErrorMessage } from '@/lib/clients';
import {
  fmtThreshold, GEOFENCE_RULE_ERROR_MESSAGES, RULE_TYPE_LABELS, useGeofenceRules, useUpdateGeofenceRule,
  type GeofenceRule, type GeofenceRuleType,
} from '@/lib/geofence-rules';
import { apiErrorCode } from '@/lib/geofences';
import { cn } from '@/lib/utils';

const TYPE_BADGE: Record<GeofenceRuleType, string> = {
  ENTER: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
  LEAVE: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200',
  SPEED_IN_ZONE: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
  DWELL: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
};

export const vehiclesSummary = (r: Pick<GeofenceRule, 'vehicleIds'>): string =>
  r.vehicleIds.length === 0 ? 'Tous les véhicules' : `${r.vehicleIds.length} véhicule${r.vehicleIds.length > 1 ? 's' : ''}`;

interface Props {
  geofenceId: string;
  canWrite: boolean;
  onCreate: () => void;
  onEdit: (rule: GeofenceRule) => void;
}

/** Règles d'alerte de la zone sélectionnée : lecture pour geofences.read, actions pour geofences.write. */
export function GeofenceRulesPanel({ geofenceId, canWrite, onCreate, onEdit }: Props) {
  const query = useGeofenceRules(geofenceId);
  const update = useUpdateGeofenceRule(geofenceId);
  const rules = query.data ?? [];

  const toggle = (r: GeofenceRule) =>
    update.mutate({ id: r.id, active: !r.active }, {
      onSuccess: (saved) => toast.success(saved.active ? 'Règle activée' : 'Règle désactivée'),
      onError: (err) => {
        const code = apiErrorCode(err);
        toast.error((code && GEOFENCE_RULE_ERROR_MESSAGES[code]) ?? apiErrorMessage(err, 'Modification impossible'));
      },
    });

  return (
    <section aria-label="Règles d'alerte" className="pt-2 mt-2 border-t space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">Règles d&apos;alerte</h2>
        {canWrite && (
          <Button size="sm" variant="outline" className="h-7 px-2" onClick={onCreate}>
            <Plus className="h-3.5 w-3.5 mr-1" aria-hidden /> Nouvelle règle
          </Button>
        )}
      </div>
      <QueryError query={query} className="flex-wrap" />
      {query.isLoading && <Skeleton className="h-12" />}
      {query.isSuccess && rules.length === 0 && (
        <p className="text-xs text-muted-foreground py-2">Aucune règle d&apos;alerte configurée pour cette zone</p>
      )}
      {rules.length > 0 && (
        <ul className="space-y-1" aria-label="Liste des règles d'alerte">
          {rules.map((r) => {
            const threshold = fmtThreshold(r);
            const body = (
              <>
                <span className="flex items-center gap-1.5 flex-wrap">
                  <Badge className={cn('border-transparent', TYPE_BADGE[r.ruleType])}>{RULE_TYPE_LABELS[r.ruleType]}</Badge>
                  {threshold && <span className="text-xs font-medium text-foreground">{threshold}</span>}
                </span>
                <span className="block text-xs text-muted-foreground mt-0.5">{vehiclesSummary(r)}</span>
              </>
            );
            return (
              <li key={r.id} className={cn('flex items-center gap-2 rounded-md border bg-background p-2', !r.active && 'opacity-60')}>
                {canWrite ? (
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left rounded hover:bg-muted"
                    aria-label={`Modifier la règle ${RULE_TYPE_LABELS[r.ruleType]}${threshold ? ` ${threshold}` : ''}`}
                    onClick={() => onEdit(r)}
                  >
                    {body}
                  </button>
                ) : (
                  <div className="min-w-0 flex-1">{body}</div>
                )}
                <Switch
                  checked={r.active}
                  disabled={!canWrite || (update.isPending && update.variables?.id === r.id)}
                  label={`${r.active ? 'Désactiver' : 'Activer'} la règle ${RULE_TYPE_LABELS[r.ruleType]}`}
                  onChange={() => toggle(r)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

interface SwitchProps {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: () => void;
  id?: string;
}

export function Switch({ checked, disabled, label, onChange, id }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={label}
      title={checked ? 'Active' : 'Inactive'}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
        checked ? 'bg-success' : 'bg-muted-foreground/30',
      )}
    >
      <span className={cn('inline-block h-4 w-4 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-4' : 'translate-x-0.5')} />
    </button>
  );
}
